import {useState, useEffect} from "react";
import {Maybe} from "type-graphql";
import {ArrayDataOptions} from "../generated/graphql";
import {Button, ButtonProps, Modal, Tooltip} from "antd";
import React from "react";
import {MoreOutlined} from "@ant-design/icons";
import moment from "moment";

export const useLocalStorageOptions = ({
    localStorageName,
    defaultOptions,
    totalResults,
}: {
    localStorageName: string;
    defaultOptions: ArrayDataOptions;
    totalResults: number;
}): [Maybe<ArrayDataOptions>, React.Dispatch<React.SetStateAction<ArrayDataOptions>>] => {
    // Initialisierung mit localStorage-Wert oder defaultOptions
    const [options, setOptions] = useState<ArrayDataOptions>(() => {
        const savedOptions = localStorage.getItem(localStorageName);
        if (savedOptions) {
            try {
                return JSON.parse(savedOptions);
            } catch (e) {
                console.error("Failed to parse localStorage options:", e);
                return defaultOptions;
            }
        }
        return defaultOptions;
    });

    // Speichere Änderungen ins localStorage
    useEffect(() => {
        localStorage.setItem(localStorageName, JSON.stringify(options));
    }, [localStorageName, options]);

    // Reset offset wenn die aktuelle Seite keine Ergebnisse mehr hätte
    useEffect(() => {
        if (totalResults && options?.offset && totalResults < options.offset) {
            setOptions((prev) => ({
                ...prev,
                offset: 0,
            }));
        }
    }, [options?.offset, totalResults]);

    return [options, setOptions];
};

export const ResetLocalStorage = ({refetch, btnContent, style, buttonProps}: {refetch?: () => void; btnContent?: string | React.ReactNode; style?: React.CSSProperties; buttonProps?: ButtonProps}) => {
    const clearAppLocalStorage = (refetch?: () => void) => {
        Object.keys(localStorage).forEach((key) => {
            localStorage.removeItem(key);
        });

        refetch?.();
    };

    return (
        <Button
            size="small"
            type="text"
            style={{padding: 0, margin: 0, ...style}}
            onClick={() =>
                Modal.confirm({
                    title: "Listenansichten zurücksetzen",
                    content: "Löscht alles, was KursOrganizer in diesem Browser gemerkt hat: Filter, Sortierung und Seitenposition der Listen, Kalenderansichten, ausgeblendete Hinweise und gemerkte Dialogeinstellungen. Deine Daten, die Spaltenauswahl und dein Login bleiben erhalten. Andere Geräte sind nicht betroffen.",
                    okText: "Jetzt Zurücksetzen",
                    onOk: () => clearAppLocalStorage(refetch),
                    icon: null,
                })
            }
            {...buttonProps}
        >
            <Tooltip title="Listenansichten zurücksetzen" placement="left">
                {btnContent ?? <MoreOutlined />}
            </Tooltip>
        </Button>
    );
};

export type WeekViewCalendarSettings = {
    currentWeek: number;
    year: number;
    timeRange: [number, number];
    locationId?: string;
    venueId?: string;
};

export const useWeekViewCalendarLocalStorage = ({
    localStorageName,
    currentWeek,
    year,
    timeRange,
    locationId,
    venueId,
}: {
    localStorageName: string;
    currentWeek: number;
    year: number;
    timeRange: [number, number];
    locationId?: string;
    venueId?: string;
}) => {
    const [settings, setSettings] = React.useState<WeekViewCalendarSettings>(() => {
        const stored = localStorage.getItem(localStorageName);
        if (stored) {
            return JSON.parse(stored);
        }
        return {
            currentWeek,
            year,
            timeRange,
            locationId,
            venueId,
        };
    });

    React.useEffect(() => {
        localStorage.setItem(localStorageName, JSON.stringify(settings));
    }, [localStorageName, settings]);

    return [settings, setSettings] as const;
};
